import { Button } from '@otter-ai/den-components';
import { getDesktopAppUtmContent } from '@otter-ai/desktop';
import { CheckoutEntryPoint, PremiumFeature } from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { PATH_URL } from '@/constants/path-constants';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { usePaywallActions } from '@/shared/modals/checkout-dialog/hooks/usePaywallActions';
import { OtterPlanType } from '@/shared/types/plan';
import { execWithWindow } from '@/shared/utils/window';
import { purchaseOpenAccountPlanUpgradeEvent } from '../tracking/tracking';
import type { ImportQuotas } from '../types/types';
import { hasImportRestrictions, importQuotaText } from '../utils/utils';

export function UploadQuotaInfo({ quota }: { quota: ImportQuotas }) {
  const { getOneClickCheckoutConfig, tryOpenPaywallDialog } =
    usePaywallActions();
  const { data: subscription } = useQuery(subscriptionQuery());
  const isFreeAccount = subscription?.plan_type === OtterPlanType.FREE;

  // If there are no import restrictions, don't show the quota info
  if (!hasImportRestrictions(quota)) {
    return null;
  }

  const checkoutConfig = getOneClickCheckoutConfig('team', true);

  function upgradePlan() {
    purchaseOpenAccountPlanUpgradeEvent(quota);
    if (
      tryOpenPaywallDialog({
        config: checkoutConfig,
        premiumFeature: isFreeAccount
          ? PremiumFeature.lifetimeImport
          : PremiumFeature.monthlyImport,
        checkoutEntryPoint: CheckoutEntryPoint.paywall,
      })
    ) {
      return;
    }

    const utmContent = getDesktopAppUtmContent();
    execWithWindow((win) => {
      const url = new URL(PATH_URL.PURCHASE_TEAM_PLAN, win.location.origin);
      url.searchParams.set('utm_content', utmContent);
      win.open(url.toString(), '_blank');
    });
  }

  return (
    <div
      data-testid="upload-quota-info"
      className="h-20 flex flex-col md:flex-row items-center justify-center border-t border-palette-gray-200 gap-0 md:gap-6 mt-auto pt-2"
    >
      <div className="text-md text-subtle p-2">{importQuotaText(quota)}</div>
      <Button
        size="lg"
        className="text-md text-primary font-semibold"
        variant="outlinePrimary"
        onClick={upgradePlan}
      >
        {checkoutConfig.dialogType === 'cc-trials'
          ? 'Try Business for free'
          : checkoutConfig.showReactivateText
            ? 'Reactivate Business for unlimited imports'
            : 'Upgrade to Business for unlimited imports'}
      </Button>
    </div>
  );
}
